import SparkMD5 from "spark-md5";
/**
 * StreamingKeyBuilder provides memory-efficient cache key generation with automatic
 * hashing for large keys. It builds keys incrementally by concatenating string parts,
 * and automatically switches to MD5 hashing when the key exceeds a threshold.
 *
 * This approach optimizes for the common case where keys are small (< 100KB) by avoiding
 * the overhead of hashing, while gracefully handling large keys by switching to a fixed-size
 * hash representation when needed.
 *
 * Key features:
 * - Incremental key building: Append string parts one at a time
 * - Threshold-based switching: Automatically switches from concatenation to hashing when size exceeds maxLength
 * - Memory efficient: Only allocates hash state when needed
 * - Deterministic: Same input always produces same output
 */
export class StreamingKeyBuilder {
    /**
     * Array of string parts accumulated before hashing threshold is reached.
     * Cleared once we switch to hashing mode to free memory.
     */
    parts = [];
    /**
     * Running total of all string lengths appended so far (in concatenation mode).
     * Used to detect when we exceed maxLength threshold.
     */
    currentLength = 0;
    /**
     * SparkMD5 hasher instance, created lazily when we exceed the threshold.
     * Null until we switch to hashing mode.
     */
    hasher = null;
    /**
     * Flag indicating whether we've switched to hashing mode.
     * Once true, all subsequent appends go directly to the hasher.
     */
    isHashing = false;
    /**
     * Maximum key length (in characters) before switching to hashing.
     * Typically set to 100KB (100 * 1000 bytes) in production.
     */
    maxLength;
    /**
     * Creates a new StreamingKeyBuilder.
     *
     * @param prefix - Initial string to prepend to the key (e.g., cache namespace)
     * @param maxLength - Maximum key length before switching to hashing (typically 100KB)
     * @param includeSpace - Whether to add a space after the prefix (default: true)
     */
    constructor(prefix, maxLength, includeSpace = true) {
        this.maxLength = maxLength;
        this.append(prefix);
        if (includeSpace) {
            this.append(" ");
        }
    }
    /**
     * Appends a string to the key being built.
     *
     * If we're still in concatenation mode and this append would exceed maxLength,
     * we switch to hashing mode by:
     * 1. Creating a SparkMD5 hasher
     * 2. Feeding all previously accumulated parts to the hasher
     * 3. Feeding the current string to the hasher
     * 4. Clearing the parts array to free memory
     *
     * If we're already in hashing mode, the string is fed directly to the hasher.
     *
     * @param str - String to append to the key
     */
    append(str) {
        if (this.isHashing) {
            this.hasher.append(str);
        }
        else {
            this.currentLength += str.length;
            if (this.currentLength > this.maxLength) {
                this.isHashing = true;
                this.hasher = new SparkMD5();
                for (const part of this.parts) {
                    this.hasher.append(part);
                }
                this.hasher.append(str);
                this.parts = [];
            }
            else {
                this.parts.push(str);
            }
        }
    }
    /**
     * Builds and returns the final cache key.
     *
     * If we're in hashing mode, returns the 32-character MD5 hex digest.
     * Otherwise, returns the concatenation of all accumulated parts.
     *
     * @returns The final cache key string
     */
    build() {
        if (this.isHashing) {
            return this.hasher.end();
        }
        return this.parts.join("");
    }
    /**
     * Returns whether the key was hashed (exceeded threshold).
     * Useful for testing and debugging.
     *
     * @returns true if the key exceeded maxLength and was hashed, false otherwise
     */
    wasHashed() {
        return this.isHashing;
    }
}
/**
 * Recursively serializes a value to a StreamingKeyBuilder with deterministic output.
 *
 * Serialization rules:
 * - Objects with 'uuid' property: Serialize as just the uuid (shortcut for common case)
 * - Arrays: Serialize as [item1,item2,item3] with comma separators
 * - Objects: Serialize as {key1:value1;key2:value2} with SORTED keys for determinism
 * - Strings: Serialize as "string" with quotes
 * - Other primitives: Serialize as String(value)
 *
 * Key sorting ensures that {a:1, b:2} and {b:2, a:1} produce the same cache key,
 * fixing cache misses that occurred with the baseline implementation.
 *
 * @param builder - The StreamingKeyBuilder to append serialized data to
 * @param el - The value to serialize (can be any type)
 */
export function serializeToBuilder(builder, el) {
    if (el && typeof el === "object" && "uuid" in el) {
        builder.append(el.uuid);
    }
    else if (Array.isArray(el)) {
        builder.append("[");
        for (let i = 0; i < el.length; i++) {
            if (i > 0)
                builder.append(",");
            serializeToBuilder(builder, el[i]);
        }
        builder.append("]");
    }
    else if (el instanceof Object) {
        builder.append("{");
        const keys = Object.keys(el).sort();
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (!key)
                continue;
            if (i > 0)
                builder.append(";");
            builder.append(key);
            builder.append(":");
            serializeToBuilder(builder, el[key]);
        }
        builder.append("}");
    }
    else if (typeof el === "string") {
        builder.append('"');
        builder.append(el);
        builder.append('"');
    }
    else {
        builder.append(String(el));
    }
}
/**
 * Serializes a value to a cache key string without hashing (backward compatibility).
 *
 * This function maintains compatibility with existing tests that expect the full
 * serialized string rather than a hash. Uses MAX_SAFE_INTEGER as threshold to
 * effectively disable hashing.
 *
 * @param el - The value to serialize
 * @returns The serialized cache key string
 */
export function createKeyUnknown(el) {
    const builder = new StreamingKeyBuilder("", Number.MAX_SAFE_INTEGER, false);
    serializeToBuilder(builder, el);
    return builder.build();
}
/**
 * Serializes an array to a cache key string without hashing (backward compatibility).
 *
 * This function maintains compatibility with existing tests. Uses MAX_SAFE_INTEGER
 * as threshold to effectively disable hashing.
 *
 * @param array - The array to serialize
 * @returns The serialized cache key string with comma-separated items
 */
export function createArrayKey(array) {
    const builder = new StreamingKeyBuilder("", Number.MAX_SAFE_INTEGER, false);
    for (let i = 0; i < array.length; i++) {
        if (i > 0)
            builder.append(",");
        serializeToBuilder(builder, array[i]);
    }
    return builder.build();
}
//# sourceMappingURL=keySerializer.js.map